import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Image,
  Spinner,
  Alert,
  ListGroup,
} from "react-bootstrap";
import { UserMinus } from "lucide-react";
import communityService from "../../../api/communityService";
import { getUser } from "../../../utils/auth";
import { toast } from "sonner";
import { FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import usePlan from "../../../hooks/usePlan";
import UserProfileModal from "../../community/components/user-profile-modal/UserProfileModal";
import RestrictionModal from "../../restriction-modals/RestrictionModal";
import { useTheme } from "../../../context/ThemeContext";

const FollowingModal = ({ show, onHide }) => {
  const { resolvedTheme } = useTheme();
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 6, // Show only 4 initially
    total: 0,
    total_pages: 0,
  });
  const [loadingMore, setLoadingMore] = useState(false);

  // Unfollow modal state
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [userToUnfollow, setUserToUnfollow] = useState(null);
  const [unfollowLoading, setUnfollowLoading] = useState(false);

  // User Profile Modal state
  const [selectedProfileUser, setSelectedProfileUser] = useState(null);
  const [showProfileUserModal, setShowProfileUserModal] = useState(false);
  const [showPaywallSheet, setShowPaywallSheet] = useState(false);

  // Get user from Redux store
  const user = useSelector((state) => state.auth?.user);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isFree } = usePlan();

  const currentUser = getUser();

  // Fetch following users
  const fetchFollowing = async (page = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const response = await communityService.getFollowing(
        currentUser.id,
        page,
        pagination.limit,
      );

      if (response?.data?.success) {
        const newFollowing = response.data.data?.following || [];
        const paginationData = response.data.data?.pagination || {};

        setFollowing((prev) =>
          append ? [...prev, ...newFollowing] : newFollowing,
        );
        setPagination(paginationData);
      }
    } catch (err) {
      console.error("Error fetching following:", err);
      setError("Failed to load following users");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more following users
  const loadMore = () => {
    if (pagination.page < pagination.total_pages && !loadingMore) {
      fetchFollowing(pagination.page + 1, true);
    }
  };

  // Handle scroll to load more
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Load more when scrolled to bottom (with 20px threshold for better responsiveness)
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      if (pagination.page < pagination.total_pages && !loadingMore) {
        loadMore();
      }
    }
  };

  // Show unfollow confirmation modal
  const handleUnfollowClick = (user) => {
    setUserToUnfollow(user);
    setShowUnfollowModal(true);
  };

  // Confirm unfollow user
  const confirmUnfollow = async () => {
    if (!userToUnfollow) return;

    setUnfollowLoading(true);

    try {
      const response = await communityService.unfollow(userToUnfollow.id);

      if (response?.data?.success) {
        // Remove the user from the following list
        setFollowing((prev) =>
          prev.filter((user) => user.id !== userToUnfollow.id),
        );

        // Update pagination total
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
        }));

        toast.success(
          `Unfollowed ${userToUnfollow.first_name} ${userToUnfollow.last_name} successfully`,
        );
        setShowUnfollowModal(false);
        setUserToUnfollow(null);
      }
    } catch (error) {
      console.error("Error unfollowing user:", error);
      toast.error("Failed to unfollow user");
    } finally {
      setUnfollowLoading(false);
    }
  };

  // Cancel unfollow
  const cancelUnfollow = () => {
    setShowUnfollowModal(false);
    setUserToUnfollow(null);
  };

  // Handle user profile click with subscription restriction
  const handleUserProfileClick = (user) => {
    // Don't open modal for own profile
    if (user.id === currentUser?.id) {
      return;
    }

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (isFree) {
      setShowPaywallSheet(true);
      return;
    }

    // Set selected user and show modal
    setSelectedProfileUser(user);
    setShowProfileUserModal(true);
  };

  // Reset state when modal opens
  useEffect(() => {
    if (show && currentUser?.id) {
      setFollowing([]);
      setError(null);
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchFollowing(1, false);
    }
  }, [show, currentUser?.id]);

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="md"
        backdrop="static"
        dialogClassName="custom-modal profile-other-details-modal"
      >
        <Modal.Header>
          <h5 className="modal-title">
            Following{pagination.total > 0 ? ` (${pagination.total})` : ""}
          </h5>
          <Button className="close-btn" onClick={onHide}>
            <FiX size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body
          style={{ maxHeight: "400px", overflowY: "auto" }}
          onScroll={handleScroll}
        >
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : following.length === 0 ? (
            <div className="text-center py-4">
              <UserMinus size={48} className="text-muted mb-3" />
              <h6>Not following anyone yet</h6>
              <p className="text-muted small">
                When you follow people, they'll appear here.
              </p>
            </div>
          ) : (
            <>
              <ListGroup variant="flush" className="user-follow-list">
                {following.map((user) => (
                  <ListGroup.Item
                    key={user.id}
                    className="px-0 py-2 border-0"
                    style={{
                      cursor:
                        user.id !== currentUser?.id ? "pointer" : "default",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div
                        className="d-flex align-items-center"
                        onClick={() => handleUserProfileClick(user)}
                        style={{
                          cursor:
                            user.id !== currentUser?.id ? "pointer" : "default",
                          flex: 1,
                        }}
                      >
                        <Image
                          src={
                            user.profile_photo_path
                              ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${user.profile_photo_path}`
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  `${user.first_name} ${user.last_name}` ||
                                    "User",
                                )}&background=0195D3&color=fff&size=40`
                          }
                          alt={`${user.first_name} ${user.last_name}`}
                          roundedCircle
                          width={40}
                          height={40}
                          className="me-3"
                          style={{
                            objectFit: "cover",
                            cursor:
                              user.id !== currentUser?.id
                                ? "pointer"
                                : "default",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              `${user.first_name} ${user.last_name}` || "User",
                            )}&background=0195D3&color=fff&size=40`;
                          }}
                        />
                        <div>
                          <h6
                            className="mb-0 small"
                            // style={{
                            //   color:
                            //     user.id !== currentUser?.id
                            //       ? "#0195D3"
                            //       : "#333",
                            //   cursor:
                            //     user.id !== currentUser?.id
                            //       ? "pointer"
                            //       : "default",
                            // }}
                            style={{
                              color: "var(--mmp-text-primary)",
                              cursor:
                                user.id !== currentUser?.id
                                  ? "pointer"
                                  : "default",
                              transition: "color 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (user.id !== currentUser?.id) {
                                e.target.style.color = "var(--mmp-accent)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = "var(--mmp-text-primary)";
                            }}
                          >
                            {user.first_name} {user.last_name}
                          </h6>
                          <small className="text-muted">@{user.username}</small>
                        </div>
                      </div>

                      {/* Unfollow Button */}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleUnfollowClick(user)}
                        style={{ fontSize: "12px", padding: "4px 12px" }}
                      >
                        Unfollow
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              {/* Loading indicator when loading more */}
              {loadingMore && (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <small className="text-muted ms-2">Loading more...</small>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Unfollow Confirmation Modal */}
      <Modal
        show={showUnfollowModal}
        onHide={cancelUnfollow}
        centered
        size="sm"
        backdrop="static"
        dialogClassName="custom-modal profile-other-details-modal unfollow-confirmation-modal"
      >
        {/* Custom backdrop with blur effect */}
        {showUnfollowModal && (
          <div
            className="modal-backdrop-blur"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor:
                resolvedTheme === "dark"
                  ? "rgba(11, 20, 24, 0.7)"
                  : "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 1040,
              pointerEvents: "none",
            }}
          />
        )}
        <div
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            position: "relative",
            zIndex: 1050,
          }}
        >
          <Modal.Header>
            <h2 className="modal-title">Unfollow User</h2>
            <Button className="close-btn" onClick={cancelUnfollow}>
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body>
            {userToUnfollow && (
              <div className="text-center">
                <Image
                  src={
                    userToUnfollow.profile_photo_path
                      ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${userToUnfollow.profile_photo_path}`
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          `${userToUnfollow.first_name} ${userToUnfollow.last_name}` ||
                            "User",
                        )}&background=0195D3&color=fff&size=60`
                  }
                  alt={`${userToUnfollow.first_name} ${userToUnfollow.last_name}`}
                  roundedCircle
                  width={60}
                  height={60}
                  className="mb-2"
                  style={{ objectFit: "cover" }}
                />
                <h6 className="mb-1">
                  {userToUnfollow.first_name} {userToUnfollow.last_name}
                </h6>
                <p className="text-muted small mb-2">
                  @{userToUnfollow.username}
                </p>
                <p className="small">
                  Are you sure you want to unfollow this user?
                </p>
              </div>
            )}

            <div className="text-center mt-4">
              <Button
                size="sm"
                variant="outline-danger"
                onClick={confirmUnfollow}
                disabled={unfollowLoading}
                style={{ fontSize: "12px", padding: "4px 12px" }}
              >
                {unfollowLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Unfollowing...
                  </>
                ) : (
                  "Unfollow"
                )}
              </Button>
            </div>
          </Modal.Body>
        </div>
      </Modal>

      {/* User Profile Modal */}
      <UserProfileModal
        show={showProfileUserModal}
        onHide={() => {
          setShowProfileUserModal(false);
          setSelectedProfileUser(null);
        }}
        userId={selectedProfileUser?.id}
        userName={`${selectedProfileUser?.first_name} ${selectedProfileUser?.last_name}`}
        userImage={selectedProfileUser?.profile_photo_path}
        userState={selectedProfileUser?.state}
      />

      {/* Subscription restriction modal */}
      <RestrictionModal
        isVisible={showPaywallSheet}
        onClose={() => setShowPaywallSheet(false)}
        eventSource="follow_limit"
        iconType="user-follow"
        heading="Want to follow or chat with this user?"
        subText="Upgrade to connect and build real relationships."
        infoText="Pro users can follow, chat, and connect with anyone in the community."
      />
    </>
  );
};

export default FollowingModal;
